import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), {
      signal,
    });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    // Media with no poster to defer behind ships in the page already loaded, so it never passes
    // through `loadContent` and has to be wired up here instead.
    if (this.#video) this.#trackVideoState(this.#video);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Follows a `<video>`'s own state. `play()` can be refused — a backgrounded tab, iOS Low Power
   * Mode, a strict autoplay policy — and the reader may also use the player's native controls, so
   * the element is the only honest source for whether it is running.
   *
   * @param {HTMLVideoElement} video
   */
  #trackVideoState(video) {
    const { signal } = this.#abortController;

    video.addEventListener('play', () => this.#setPlaybackState(true), { signal });
    video.addEventListener('pause', () => this.#setPlaybackState(false), { signal });
  }

  /**
   * A `<video>` reports its own state through `play`/`pause` events, so it is the authority on
   * whether it is running. A cross-origin `<iframe>` player is opaque — commands go out over
   * `postMessage` and nothing comes back — so its state has to be assumed.
   */
  get #video() {
    return this.querySelector('video');
  }

  get #iframePlayer() {
    return /** @type {HTMLIFrameElement | null} */ (this.querySelector('iframe[data-video-type]'));
  }

  /**
   * Sends a transport command to an embedded player. No-op when there is no iframe — a 3D model
   * reaches its own player through `ProductModel`, which extends these two methods.
   * @param {'play' | 'pause'} action
   */
  #commandIframePlayer(action) {
    const iframe = this.#iframePlayer;
    if (!iframe) return;

    const message =
      iframe.dataset.videoType === 'youtube'
        ? { event: 'command', func: action === 'play' ? 'playVideo' : 'pauseVideo', args: '' }
        : { method: action };

    iframe.contentWindow?.postMessage(JSON.stringify(message), '*');
  }

  /**
   * Records the playback state and points the hint button's label at the action it now performs.
   * The button's accessible name comes from whichever icon is showing, so this is what tells a
   * screen reader the state changed.
   *
   * @param {boolean} isPlaying - Whether the media is currently playing.
   */
  #setPlaybackState(isPlaying) {
    this.isPlaying = isPlaying;

    const button = this.refs.toggleMediaButton;
    if (!(button instanceof HTMLElement)) return;

    const playIcon = button.querySelector('[data-media-icon="play"]');
    if (playIcon instanceof HTMLElement) playIcon.hidden = isPlaying;

    const pauseIcon = button.querySelector('[data-media-icon="pause"]');
    if (pauseIcon instanceof HTMLElement) pauseIcon.hidden = !isPlaying;
  }

  /**
   * Reveals the embed if it has not loaded yet, then plays it.
   * @param {boolean} focus - Whether the embed may take focus. Only when the reader asked for it.
   */
  #reveal(focus) {
    if (this.getAttribute('data-media-loaded')) {
      this.playMedia();
      return;
    }

    this.loadContent(focus);

    // The embed starts itself — a `<video>` from its `autoplay` attribute, an iframe from
    // `autoplay=1` in its src — so calling `playMedia` here as well would land a second `play()`
    // in the same tick and abort the first. From here a `<video>` mirrors its own events; an
    // iframe has none, so take it at its word.
    if (!this.#video) this.#setPlaybackState(true);
  }

  /**
   * Reveals and plays the media because the reader asked for it.
   */
  showDeferredMedia = () => {
    const focusedBefore = document.activeElement;

    this.#reveal(true);

    // The poster the reader just activated hides itself, so focus has to be handed on
    // deliberately. `loadContent` offers focus to the embed, but a `<video>` rendered without
    // controls refuses it — and the browser then drops the reader on `<body>` the moment the
    // poster turns `visibility: hidden`. If the embed did take focus, leave it alone.
    if (document.activeElement === focusedBefore) this.refs.toggleMediaButton?.focus();
  };

  /**
   * Reveals and plays the media because it scrolled into view, rather than because the reader
   * asked. Never moves focus: that would yank them out of the carousel they are scrolling.
   */
  playInView() {
    this.#reveal(false);
  }

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    // The poster hides itself off this attribute, and the hint button replaces it.
    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);
    if (this.refs.toggleMediaButton instanceof HTMLElement) {
      this.refs.toggleMediaButton.hidden = false;
    }

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    if (content instanceof HTMLVideoElement) {
      this.#trackVideoState(content);

      if (content.getAttribute('autoplay')) {
        // force autoplay for safari
        content.play().catch(() => {
          // Refused. The listeners above leave the hint showing "play", which is the truth.
        });
      }
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    // No state written on the `<video>` path: its own `play` event does it, so a refused or
    // superseded request can never leave the hint claiming the video is running.
    if (this.#video) {
      this.#video.play().catch(() => {});
      return;
    }

    this.#commandIframePlayer('play');
    this.#setPlaybackState(true);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    if (this.#video) {
      this.#video.pause();
      return;
    }

    this.#commandIframePlayer('pause');
    this.#setPlaybackState(false);
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error | undefined} error
   */
  async setupModelViewerUI(error) {
    if (error) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      /** @type {EventListener} */ (
        /** @type {unknown} */ (
          (/** @type {PointerEvent} */ event) => {
            pointerStartX = event.clientX;
            pointerStartY = event.clientY;
          }
        )
      ),
      { signal },
    );

    element.addEventListener(
      'click',
      /** @type {EventListener} */ (
        /** @type {unknown} */ (
          (/** @type {PointerEvent} */ event) => {
            const distanceX = Math.abs(event.clientX - pointerStartX);
            const distanceY = Math.abs(event.clientY - pointerStartY);
            const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

            // Try to ensure that this is a tap, not a drag.
            if (totalDistance < 10) {
              // When the model is paused, it has its own button overlay for playing the model again.
              // If we're receiving a click event, it means the model is playing, all we can do is pause it.
              this.pauseMedia();
            }
          }
        )
      ),
      { signal },
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
